import * as THREE from "three";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils.js";

export const DROBBIT_GLB = "/assets/drobbit.glb";
export const DESIRED_HEIGHT = 0.9;
export const DROBBIT_WAVE_DURATION_MS = 1400;
const NON_TINT_MATERIAL_NAMES = new Set(["Material.007"]);

export type DrobbitAnimationMode =
  | "idle"
  | "walk"
  | "dance"
  | "wave"
  | "sit";

function findAnimationClipByName(
  animations: THREE.AnimationClip[],
  target: string
): THREE.AnimationClip | undefined {
  const normalizedTarget = target.trim().toLowerCase();
  if (!normalizedTarget) return undefined;
  const exact = animations.find(
    (clip) => clip.name.trim().toLowerCase() === normalizedTarget
  );
  if (exact) return exact;
  return animations.find((clip) =>
    clip.name.trim().toLowerCase().includes(normalizedTarget)
  );
}

export function findDrobbitActionClips(animations: THREE.AnimationClip[]): {
  walk?: THREE.AnimationClip;
  dance?: THREE.AnimationClip;
  wave?: THREE.AnimationClip;
  sit?: THREE.AnimationClip;
} {
  const clean = animations.filter(
    (clip) => !clip.name.toLowerCase().includes("[action stash]")
  );
  const pool = clean.length > 0 ? clean : animations;
  const walkByKeyword = pool.find((clip) => /\bwalk\b/i.test(clip.name));
  const danceByKeyword = pool.find((clip) => /\bdance\b/i.test(clip.name));
  const waveByKeyword = pool.find((clip) => /\bwave\b/i.test(clip.name));
  const sitByKeyword = pool.find((clip) =>
    /\bsit\b|\bsitting\b/i.test(clip.name)
  );
  const walk =
    findAnimationClipByName(pool, "Walk") ??
    walkByKeyword ??
    pool[0];
  const dance =
    findAnimationClipByName(pool, "Dance") ??
    danceByKeyword ??
    pool[Math.min(1, Math.max(0, pool.length - 1))];
  const wave =
    findAnimationClipByName(pool, "Wave") ??
    waveByKeyword ??
    undefined;
  const sit =
    findAnimationClipByName(pool, "Sitting") ??
    findAnimationClipByName(pool, "Sit") ??
    sitByKeyword ??
    undefined;

  return {
    walk,
    dance,
    wave,
    sit,
  };
}

function createTintedMaterial(
  source: THREE.Material,
  playerColor: THREE.Color
): THREE.Material {
  const orig = source as THREE.MeshStandardMaterial;
  const origColor = orig.color ?? new THREE.Color(0xffffff);
  const preserveColor = NON_TINT_MATERIAL_NAMES.has(source.name ?? "");
  const isVeryDark =
    origColor.r < 0.15 && origColor.g < 0.15 && origColor.b < 0.15;

  const baseColor = preserveColor
    ? origColor.clone()
    : isVeryDark
    ? playerColor.clone()
    : origColor.clone().lerp(playerColor, 0.6);

  const tinted = source.clone() as THREE.Material & {
    color?: THREE.Color;
    skinning?: boolean;
    morphTargets?: boolean;
    morphNormals?: boolean;
    needsUpdate?: boolean;
  };

  if (tinted.color?.isColor) {
    tinted.color.copy(baseColor);
  }

  // Keep all source shader features while ensuring animated avatars still bind
  // skin/morph tracks after tinting.
  tinted.skinning = (orig as unknown as { skinning?: boolean }).skinning ?? true;
  tinted.morphTargets =
    (orig as unknown as { morphTargets?: boolean }).morphTargets ?? false;
  tinted.morphNormals =
    (orig as unknown as { morphNormals?: boolean }).morphNormals ?? false;
  tinted.needsUpdate = true;

  return tinted;
}

function computeGroundAnchor(group: THREE.Group): {
  x: number;
  y: number;
  z: number;
} {
  group.updateMatrixWorld(true);

  const sampled: Array<{ x: number; y: number; z: number }> = [];

  const local = new THREE.Vector3();
  const world = new THREE.Vector3();
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  group.traverse((child) => {
    if (!(child as THREE.Mesh).isMesh) return;
    const mesh = child as THREE.Mesh;
    const position = mesh.geometry?.attributes?.position;
    if (!position) return;
    // Sample very dense meshes to keep avatar construction lightweight.
    const step = position.count > 12000 ? 2 : 1;
    for (let i = 0; i < position.count; i += step) {
      mesh.getVertexPosition(i, local);
      world.copy(local).applyMatrix4(mesh.matrixWorld);
      sampled.push({ x: world.x, y: world.y, z: world.z });
      minX = Math.min(minX, world.x);
      maxX = Math.max(maxX, world.x);
      minY = Math.min(minY, world.y);
      maxY = Math.max(maxY, world.y);
      minZ = Math.min(minZ, world.z);
      maxZ = Math.max(maxZ, world.z);
    }
  });

  if (!sampled.length) {
    const fallbackBox = new THREE.Box3().setFromObject(group, true);
    const center = new THREE.Vector3();
    fallbackBox.getCenter(center);
    return {
      x: center.x,
      y: fallbackBox.min.y,
      z: center.z,
    };
  }

  const sortedY = sampled
    .map((vertex) => vertex.y)
    .sort((a, b) => a - b);
  // Ignore extreme low outliers from rig artifacts.
  const floorIndex = Math.floor((sortedY.length - 1) * 0.01);
  const floorY = sortedY[floorIndex];
  const height = maxY - minY;
  const footBandTop = floorY + Math.max(0.06, height * 0.22);
  let footCount = 0;
  let footSumX = 0;
  let footSumZ = 0;

  for (const vertex of sampled) {
    if (vertex.y > footBandTop) continue;
    footCount += 1;
    footSumX += vertex.x;
    footSumZ += vertex.z;
  }

  if (footCount === 0) {
    return {
      x: (minX + maxX) * 0.5,
      y: floorY,
      z: (minZ + maxZ) * 0.5,
    };
  }

  return {
    x: footSumX / footCount,
    y: floorY,
    z: footSumZ / footCount,
  };
}

/**
 * Build a tinted clone of the avatar GLB.
 * Dark parts of the original mesh become the player color,
 * light parts blend 60% toward it.
 */
export function buildTintedModel(
  sourceScene: THREE.Group,
  color: string
): { group: THREE.Group; height: number } {
  const group = SkeletonUtils.clone(sourceScene) as THREE.Group;

  const box = new THREE.Box3().setFromObject(group);
  const size = new THREE.Vector3();
  box.getSize(size);
  const scale = size.y > 0 ? DESIRED_HEIGHT / size.y : 1;
  group.scale.set(scale, scale, scale);

  const anchor = computeGroundAnchor(group);
  group.position.set(-anchor.x, -anchor.y, -anchor.z);

  const playerColor = new THREE.Color(color);

  group.traverse((child) => {
    if (!(child as THREE.Mesh).isMesh) return;
    const mesh = child as THREE.Mesh;
    mesh.castShadow = false;
    mesh.receiveShadow = false;

    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map((material) =>
          createTintedMaterial(material, playerColor)
        )
      : createTintedMaterial(mesh.material, playerColor);
  });

  return { group, height: size.y * scale };
}
